import { useEffect, useRef, useState } from 'react'
import { asset } from '../lib/assets'
import { useTerms } from '../i18n/TermsContext'
import { Icon } from './Icon'

interface ChromeUser {
  name: string
  email?: string
  role?: string
}

const initials = (name: string): string =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || 'U'

const SETTINGS_ROLES = new Set(['admin', 'manager', 'owner', 'apiclient'])

// usuário pode ter mais de um papel no mesmo provider (ex.: "Atendente,Manager"); qualquer
// separador comum é aceito e basta UM dos papéis bater para liberar o acesso.
const rolesOf = (role?: string): string[] =>
  (role ?? '').split(/[,/;]+/).map((r) => r.trim().toLowerCase()).filter(Boolean)

export const canAccessSettings = (role?: string): boolean =>
  rolesOf(role).some((r) => SETTINGS_ROLES.has(r))

export const isOwnerRole = (role?: string): boolean => rolesOf(role).includes('owner')

export function Sidebar({ active, onSelect, activeCount, providerName, locationName, user, onRequestChangeScope, onLogout, isOpen, onClose }: {
  active: string
  onSelect: (id: string) => void
  activeCount: number
  providerName: string
  locationName: string
  user: ChromeUser
  onRequestChangeScope: () => void
  onLogout: () => void
  isOpen?: boolean
  onClose?: () => void
}) {
  const sidebarRef = useRef<HTMLElement>(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!isOpen) {return}
    const mobile = window.matchMedia('(max-width: 900px)')
    if (!mobile.matches) {return}
    const previousOverflow = document.body.style.overflow
    const previousFocus = document.activeElement as HTMLElement | null
    document.body.style.overflow = 'hidden'
    sidebarRef.current?.querySelector<HTMLButtonElement>('.fz-sidebar__close')?.focus({ preventScroll: true })

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onCloseRef.current?.()
      } else if (event.key === 'Tab') {
        const buttons = sidebarRef.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)')
        if (!buttons?.length) {return}
        const first = buttons[0]
        const last = buttons[buttons.length - 1]
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault()
          last.focus()
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault()
          first.focus()
        }
      }
    }
    const onViewportChange = () => {if (!mobile.matches) {onCloseRef.current?.()}}
    window.addEventListener('keydown', onKeyDown)
    mobile.addEventListener('change', onViewportChange)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', onKeyDown)
      mobile.removeEventListener('change', onViewportChange)
      // Safari não foca o botão ao tocar nele; devolve o foco ao acionador do menu.
      const menuButton = document.querySelector<HTMLButtonElement>('.fz-topbar__menu')
      const returnFocus = menuButton ?? previousFocus
      if (returnFocus?.isConnected) {returnFocus.focus({ preventScroll: true })}
    }
  }, [isOpen])

  const items = [
    { id: 'lista',    label: 'Atendimentos', icon: 'list',  badge: activeCount },
    { id: 'operador', label: 'Painel do operador', icon: 'users' },
    { id: 'criar',    label: 'Novo atendimento', icon: 'plus' },
  ]
  const config = [
    { id: 'modelos', label: 'Modelos de Jornada', icon: 'list' },
    ...(isOwnerRole(user.role) ? [{ id: 'custom-fields', label: 'Campos Customizados', icon: 'list' }] : []),
  ]
  return (
    <>
      {isOpen && <div className="fz-sidebar__backdrop" onClick={onClose} aria-hidden="true" />}
      <aside
        ref={sidebarRef}
        className={'fz-sidebar' + (isOpen ? ' is-open' : '')}
        data-screen-label="Sidebar"
        {...(isOpen ? { role: 'dialog', 'aria-modal': true, 'aria-label': 'Menu de navegação' } : {})}
      >
        <div className="fz-sidebar__brand">
        <img src={asset('favicon-fi-480.png')} alt="" width={30} height={30} />
        <div>
          <div className="fz-sidebar__org">{providerName}</div>
          <div className="fz-sidebar__sub">{locationName}</div>
        </div>
        <button type="button" className="fz-sidebar__close fz-iconbtn" onClick={onClose} aria-label="Fechar menu">
          <Icon name="x" size={18} />
        </button>
      </div>
      <nav className="fz-sidebar__nav">
        <div className="fz-sidebar__group">Filazero — Jornada</div>
        {items.map((it) => (
          <button key={it.id} className={'fz-sidebar__item ' + (active === it.id ? 'is-active' : '')} onClick={() => onSelect(it.id)}>
            <Icon name={it.icon} size={18} />
            <span>{it.label}</span>
            {it.badge != null && <span className="fz-sidebar__badge">{it.badge}</span>}
          </button>
        ))}
        {canAccessSettings(user.role) && (
          <>
            <div className="fz-sidebar__group">Configuração</div>
            {config.map((it) => (
              <button key={it.id} className={'fz-sidebar__item ' + (active === it.id ? 'is-active' : '')} onClick={() => onSelect(it.id)}>
                <Icon name={it.icon} size={18} />
                <span>{it.label}</span>
              </button>
            ))}
          </>
        )}
      </nav>
      <div className="fz-sidebar__foot">
        <div className="fz-sidebar__user">
          <div className="fz-sidebar__avatar">{initials(user.name)}</div>
          <div>
            <div className="fz-sidebar__username">{user.name}</div>
            <div className="fz-sidebar__userrole">{user.email ?? 'Usuário autenticado'}</div>
          </div>
        </div>
        <div className="fz-sidebar__actions">
          <button className="fz-sidebar__scope" onClick={onRequestChangeScope}>
            <Icon name="buildingTwo" size={15} />
            <span>Alterar unidade</span>
          </button>
          <button className="fz-sidebar__logout" onClick={onLogout}>
            <Icon name="arrowR" size={14} />
            <span>Sair</span>
          </button>
        </div>
      </div>
    </aside>
  </>
  )
}

export function Topbar({ search, onSearch, contextLabel, onMenuOpen }: {
  search: string
  onSearch: (v: string) => void
  contextLabel?: string
  onMenuOpen?: () => void
}) {
  const terms = useTerms()
  const [isDark, setIsDark] = useState<boolean>(() => {
    if (typeof document === 'undefined') {return false}
    return document.documentElement.classList.contains('dark-mode')
  })

  const toggleTheme = () => {
    const nextDark = !isDark
    setIsDark(nextDark)
    if (nextDark) {
      document.documentElement.classList.add('dark-mode')
      document.documentElement.classList.remove('light-mode')
      try { localStorage.setItem('fz-theme', 'dark') } catch { /* localStorage indisponível (modo privado) */ }
    } else {
      document.documentElement.classList.remove('dark-mode')
      document.documentElement.classList.add('light-mode')
      try { localStorage.setItem('fz-theme', 'light') } catch { /* localStorage indisponível (modo privado) */ }
    }
  }

  return (
    <header className="fz-topbar" data-screen-label="Topbar">
      <button className="fz-topbar__menu fz-iconbtn" onClick={onMenuOpen} aria-label="Abrir menu">
        <Icon name="menu" size={18} />
      </button>
      <div className="fz-topbar__search">
        <Icon name="search" size={16} />
        <input value={search} onChange={(e) => onSearch(e.target.value)} placeholder={`Buscar ${terms.subjectLower} por nome, CPF, telefone ou código…`} aria-label={`Buscar ${terms.subjectLower}`} autoComplete="off" name="topbar-search" />
        {search && <button type="button" className="fz-topbar__search-clear" onClick={() => onSearch('')} aria-label="Limpar busca"><Icon name="x" size={15} /></button>}
      </div>
      <div className="fz-topbar__spacer" />
      <button className="fz-iconbtn" onClick={toggleTheme} aria-label="Alternar tema">
        <Icon name={isDark ? 'sun' : 'moon'} size={16} />
      </button>
      {contextLabel && <div className="fz-contextpill" title={contextLabel}>{contextLabel}</div>}
    </header>
  )
}
